package top.wcpe.allincore.ui.runtime.render.model

import kotlin.jvm.JvmName
import kotlin.jvm.JvmStatic

/**
 * 不可变渲染命令值对象，描述一次绘制意图。
 *
 * 分桶键由 [type]、[texture]、[clipId] 共同决定。
 *
 * ADR-0072 决策 1 镜像：真源为 `platform-client-api` 的
 * `top.wcpe.allincore.platform.client.api.render.command.RenderCommand`
 * （纯 Java 8 对外 API，按 ADR-0002 不得 KMP 化）。真源为 `@Getter` 值对象（无 equals），
 * 镜像同样不重写 equals；字段面（23 字段）、静态工厂与 [translated] 逻辑逐一照抄真源。
 * 构造器可见性为 internal（真源 private）：仅供本文件工厂与 jvmMain 集中转换层
 * RenderModelConverters 使用，模块外仍只能经工厂构造。
 * 一致性由 jvmTest 的 RenderModelConsistencyTest 逐工厂逐字段 roundtrip 守护。
 */
// 字段面（23 字段）与工厂面（14 工厂）照抄真源：命令载荷本质是「按命令类型取用」的宽值对象，
// 工厂数 = 命令类型词汇表，由类型清单驱动而非业务堆积，故豁免 LongParameterList / TooManyFunctions。
@Suppress("LongParameterList", "TooManyFunctions")
class RenderCommand internal constructor(
    @get:JvmName("type") val type: RenderCommandType,
    @get:JvmName("x") val x: Float,
    @get:JvmName("y") val y: Float,
    @get:JvmName("w") val w: Float,
    @get:JvmName("h") val h: Float,
    @get:JvmName("argb") val argb: Int,
    @get:JvmName("texture") val texture: String?,
    @get:JvmName("clipId") val clipId: Int,
    @get:JvmName("text") val text: String?,
    @get:JvmName("scale") val scale: Float,
    @get:JvmName("styledFill") val styledFill: StyledFill?,
    @get:JvmName("shadow") val shadow: ShadowParams?,
    @get:JvmName("imageStyle") val imageStyle: ImageStyle?,
    // 物品/贴图切片/实体预览扩展字段（真源 Stage A1 新增）；默认值等价真源 13 参私有便捷构造
    @get:JvmName("itemId") val itemId: String? = null,
    /** 物品 NBT（平台无关 SNBT 字符串）；仅 DRAW_ITEM_STACK 有效，null/空表示无 NBT */
    @get:JvmName("itemNbt") val itemNbt: String? = null,
    @get:JvmName("itemCount") val itemCount: Int = 0,
    @get:JvmName("uvX") val uvX: Float = 0f,
    @get:JvmName("uvY") val uvY: Float = 0f,
    @get:JvmName("uvW") val uvW: Float = 0f,
    @get:JvmName("uvH") val uvH: Float = 0f,
    @get:JvmName("entityRef") val entityRef: String? = null,
    @get:JvmName("mouseX") val mouseX: Float = 0f,
    @get:JvmName("mouseY") val mouseY: Float = 0f,
) {

    /**
     * 返回与本命令完全相同、但 x/y 平移 (dx, dy) 的新命令。
     *
     * 用于装饰器（如 OffsetRenderBridge）转发 batch 时给坐标加偏移，避免逐 case 重建。
     */
    fun translated(dx: Float, dy: Float): RenderCommand {
        if (dx == 0f && dy == 0f) {
            return this
        }
        // DRAW_LINE 的 w/h 实际表示线段终点的 (x2,y2)，需要同时平移
        val nw = if (type == RenderCommandType.DRAW_LINE) w + dx else w
        val nh = if (type == RenderCommandType.DRAW_LINE) h + dy else h
        return RenderCommand(
            type, x + dx, y + dy, nw, nh,
            argb, texture, clipId, text, scale, styledFill, shadow, imageStyle,
            itemId, itemNbt, itemCount, uvX, uvY, uvW, uvH, entityRef, mouseX, mouseY,
        )
    }

    companion object {

        /** 无裁剪区域时的 clipId 值 */
        const val NO_CLIP: Int = -1

        /** 创建填充矩形命令 */
        @JvmStatic
        fun fillRect(x: Float, y: Float, w: Float, h: Float, argb: Int, clipId: Int): RenderCommand =
            RenderCommand(RenderCommandType.FILL_RECT, x, y, w, h, argb, null, clipId, null, 0f, null, null, null)

        /** 创建绘制文本命令 */
        @JvmStatic
        fun drawText(text: String?, x: Float, y: Float, argb: Int, scale: Float, clipId: Int): RenderCommand =
            RenderCommand(RenderCommandType.DRAW_TEXT, x, y, 0f, 0f, argb, null, clipId, text, scale, null, null, null)

        /** 创建绘制线段命令 */
        @JvmStatic
        fun drawLine(x1: Float, y1: Float, x2: Float, y2: Float, argb: Int, clipId: Int): RenderCommand =
            RenderCommand(RenderCommandType.DRAW_LINE, x1, y1, x2, y2, argb, null, clipId, null, 0f, null, null, null)

        /** 创建绘制贴图命令 */
        @JvmStatic
        fun drawImage(texture: String?, x: Float, y: Float, w: Float, h: Float, clipId: Int): RenderCommand =
            RenderCommand(RenderCommandType.DRAW_IMAGE, x, y, w, h, 0, texture, clipId, null, 0f, null, null, null)

        /** 创建带样式圆角矩形命令 */
        @JvmStatic
        fun roundedRect(
            x: Float,
            y: Float,
            w: Float,
            h: Float,
            argb: Int,
            clipId: Int,
            styledFill: StyledFill?,
        ): RenderCommand = RenderCommand(
            RenderCommandType.ROUNDED_RECT, x, y, w, h, argb, null, clipId, null, 0f, styledFill, null, null,
        )

        /** 创建渐变填充命令 */
        @JvmStatic
        fun gradient(
            x: Float,
            y: Float,
            w: Float,
            h: Float,
            argb: Int,
            clipId: Int,
            styledFill: StyledFill?,
        ): RenderCommand =
            RenderCommand(RenderCommandType.GRADIENT, x, y, w, h, argb, null, clipId, null, 0f, styledFill, null, null)

        /** 创建阴影绘制命令 */
        @JvmStatic
        fun shadow(x: Float, y: Float, w: Float, h: Float, clipId: Int, shadow: ShadowParams): RenderCommand =
            RenderCommand(
                RenderCommandType.SHADOW, x, y, w, h, shadow.color, null, clipId, null, 0f, null, shadow, null,
            )

        /** 创建带圆角信息的阴影绘制命令（GPU shader 需要 CornerRadii 来渲染圆角阴影）。 */
        @JvmStatic
        fun shadow(
            x: Float,
            y: Float,
            w: Float,
            h: Float,
            clipId: Int,
            shadow: ShadowParams,
            styledFill: StyledFill?,
        ): RenderCommand = RenderCommand(
            RenderCommandType.SHADOW, x, y, w, h, shadow.color, null, clipId, null, 0f, styledFill, shadow, null,
        )

        /** 创建带纹理变换样式的贴图命令 */
        @JvmStatic
        fun imageStyled(
            resource: String?,
            x: Float,
            y: Float,
            w: Float,
            h: Float,
            clipId: Int,
            imageStyle: ImageStyle?,
        ): RenderCommand = RenderCommand(
            RenderCommandType.IMAGE_STYLED, x, y, w, h, 0, resource, clipId, null, 0f, null, null, imageStyle,
        )

        /** 创建绘制物品图标命令（含数量徽标）。 */
        @JvmStatic
        fun drawItemStack(
            itemId: String?,
            count: Int,
            x: Float,
            y: Float,
            w: Float,
            h: Float,
            clipId: Int,
        ): RenderCommand = drawItemStack(itemId, null, count, x, y, w, h, clipId)

        /** 创建绘制物品图标命令（携带 SNBT 物品 NBT，用于自定义模型/贴图解析）。 */
        @JvmStatic
        fun drawItemStack(
            itemId: String?,
            itemNbt: String?,
            count: Int,
            x: Float,
            y: Float,
            w: Float,
            h: Float,
            clipId: Int,
        ): RenderCommand = RenderCommand(
            RenderCommandType.DRAW_ITEM_STACK, x, y, w, h, 0, null, clipId, null, 0f,
            null, null, null,
            itemId, itemNbt, count,
        )

        /** 创建绘制贴图子区域（uv 切片）命令。 */
        @JvmStatic
        fun drawTexturedQuad(
            resource: String?,
            x: Float,
            y: Float,
            w: Float,
            h: Float,
            uvX: Float,
            uvY: Float,
            uvW: Float,
            uvH: Float,
            clipId: Int,
        ): RenderCommand = RenderCommand(
            RenderCommandType.DRAW_TEXTURED_QUAD, x, y, w, h, 0, resource, clipId, null, 0f,
            null, null, null,
            null, null, 0, uvX, uvY, uvW, uvH,
        )

        /** 创建绘制 2D 实体预览命令。 */
        @JvmStatic
        fun drawEntityPreview(
            entityRef: String?,
            x: Float,
            y: Float,
            w: Float,
            h: Float,
            mouseX: Float,
            mouseY: Float,
            clipId: Int,
        ): RenderCommand = RenderCommand(
            RenderCommandType.DRAW_ENTITY_PREVIEW, x, y, w, h, 0, null, clipId, null, 0f,
            null, null, null,
            null, null, 0, 0f, 0f, 0f, 0f, entityRef, mouseX, mouseY,
        )
    }
}
